'use client';

import { useUser } from '@auth0/nextjs-auth0';
import { findUserByAuth0 } from 'actions/Database';
import { handleDevUserUpdate } from 'actions/DevTools';
import { findZipcode } from 'actions/Locations';
import { DBUser } from 'db/schema/users';
import { useAppDispatch } from 'lib/hooks';
import type { APILocation } from 'lib/MedicareApi';
import { useEffect, useState } from 'react';
import { setUserDatabase } from 'slices/userSlice';
import { TaxFilingStatus } from 'types/user';

const DevUserProfile = () => {
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const [dbUser, setDbUser] = useState<DBUser | null>(null);
  const [zipDraft, setZipDraft] = useState('');
  const [locChoices, setLocChoices] = useState<APILocation[] | null>(null);
  const [pickedCountyFips, setPickedCountyFips] = useState('');
  const [zipLookupError, setZipLookupError] = useState<string | null>(null);

  const handleFormSubmit = async (formData: FormData) => {
    if (user) {
      const updated = await handleDevUserUpdate(formData);

      if (updated) {
        dispatch(
          setUserDatabase({ ...updated, created_at: null, updated_at: null })
        );
        setDbUser(updated);
        setZipDraft(updated.zipcode ?? '');
        setLocChoices(null);
        setZipLookupError(null);
      }
    }
  };

  useEffect(() => {
    if (!dbUser && user) {
      findUserByAuth0().then((found) => {
        if (found) {
          setDbUser(found);
          if (found.zipcode) {
            setZipDraft(found.zipcode);
          }
        }
      });
    }
  }, [dbUser, user]);

  const selectedLocation =
    locChoices?.find((l) => l.county_fips === pickedCountyFips) ?? null;

  const handleZipLookup = async () => {
    const z = zipDraft.trim();
    setZipLookupError(null);

    if (!/^[0-9]{5}$/.test(z)) {
      setZipLookupError('Enter a valid 5-digit ZIP code.');
      setLocChoices(null);
      return;
    }

    try {
      const { locations } = await findZipcode(z);
      if (!locations?.length) {
        setZipLookupError('No locations found for this ZIP.');
        setLocChoices(null);
        return;
      }

      setLocChoices(locations);
      if (locations.length === 1) {
        setPickedCountyFips(locations[0].county_fips);
      } else {
        const match = locations.find(
          (l) => l.county_fips === dbUser?.county_fips
        );
        setPickedCountyFips(match?.county_fips ?? locations[0].county_fips);
      }
    } catch {
      setZipLookupError('ZIP lookup failed.');
      setLocChoices(null);
    }
  };

  if (!user || !dbUser) {
    return null;
  }

  return (
    <section>
      <h3>User Profile</h3>

      <form action={handleFormSubmit}>
        <div className="stack">
          <div>Id: {dbUser.id}</div>
          <div>Medicare Number: {dbUser.medicare_number}</div>
          <div>
            <label>
              ZIP code
              <input
                name="zipcode"
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={5}
                pattern="[0-9]{5}"
                value={zipDraft}
                onChange={(e) => {
                  setZipDraft(e.target.value);
                  setLocChoices(null);
                  setZipLookupError(null);
                }}
              />
            </label>
            <button type="button" onClick={handleZipLookup}>
              Look up counties
            </button>
            {zipLookupError && <div role="alert">{zipLookupError}</div>}
          </div>
          {locChoices && locChoices.length > 1 && (
            <div>
              <label>
                County
                <select
                  value={pickedCountyFips}
                  onChange={(e) => setPickedCountyFips(e.target.value)}
                >
                  {locChoices.map((loc) => (
                    <option key={loc.county_fips} value={loc.county_fips}>
                      {loc.county_name}, {loc.state}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {selectedLocation && (
            <>
              <input
                type="hidden"
                name="county_fips"
                value={selectedLocation.county_fips}
              />
              <input
                type="hidden"
                name="state"
                value={selectedLocation.state}
              />
              <input
                type="hidden"
                name="county_name"
                value={selectedLocation.county_name}
              />
            </>
          )}
          <div>County Name: {dbUser.county_name}</div>
          <div>County Fips: {dbUser.county_fips}</div>
          <div>State: {dbUser.state}</div>
          <div>
            <label>
              Birthdate
              <input
                type="date"
                name="birthdate"
                defaultValue={dbUser.birthdate as string}
              />
            </label>
          </div>
          <div>
            <label>
              Gender
              <select name="gender" defaultValue={dbUser.gender as string}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </label>
          </div>
          <div>
            <label>
              Tobacco
              <select
                name="tobacco"
                defaultValue={dbUser.tobacco === true ? 'true' : 'false'}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </label>
          </div>
          <div>
            <label>
              Primary Care Visits
              <input
                type="number"
                name="pcp_visits"
                defaultValue={dbUser.pcp_visits || 0}
              />
            </label>
          </div>
          <div>
            <label>
              Specialist Visits
              <input
                type="number"
                name="specialist_visits"
                defaultValue={dbUser.specialist_visits || 0}
              />
            </label>
          </div>
          <div>
            <label>
              Hospital Visits
              <input
                type="number"
                name="hospital_visits"
                defaultValue={dbUser.hospital_visits || 0}
              />
            </label>
          </div>
          <div>
            <label>
              Income
              <input
                type="number"
                name="income"
                defaultValue={dbUser.income || 0}
              />
            </label>
          </div>
          <div>
            <label>
              Filing Status
              <select
                name="filing_status"
                defaultValue={dbUser.filing_status || ''}
              >
                <option value={TaxFilingStatus.Single}>Individual</option>
                <option value={TaxFilingStatus.MarriedFilingSeparately}>
                  Married Filing Separately
                </option>
                <option value={TaxFilingStatus.MarriedFilingJointly}>
                  Married Filing Jointly
                </option>
              </select>
            </label>
          </div>
          <div>
            <label>
              Hearing Aids?
              <select
                name="hearing_aids"
                defaultValue={dbUser.hearing_aids === true ? 'true' : 'false'}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </label>
          </div>
          <div>
            <button type="submit" className="button primary">
              Save
            </button>
          </div>
        </div>
      </form>
    </section>
  );
};

export default DevUserProfile;
